import React, { useEffect, useState } from "react";

/**
 * Attesa della risposta, in due pezzi che vivono in punti diversi del messaggio:
 *
 *   <TypingMark />   il logo LF23 animato, FUORI dalla bolla, a sinistra come un
 *                    avatar (dentro .chatbot-msg, prima di .chatbot-msg-inner).
 *   <TypingLabel />  l'etichetta "sta scrivendo…" / "Sto leggendo la pagina…",
 *                    DENTRO la bolla al posto del testo che deve ancora arrivare.
 *
 * Il logo non è uno spinner aggiunto: la bolla del marchio ha già tre puntini
 * bianchi disegnati dentro, che qui si accendono a turno mentre la bolla respira.
 * Così anche le attese lunghe (una lettura web, la generazione di un file) si
 * vedono, invece di sembrare un blocco.
 */

/** Logo LF23 animato. `size` in px (default 34: più grande di quello in sidebar). */
export function TypingMark({ size = 34 }) {
    return (
        <svg
            className="chatbot-typing-mark"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
        >
            <defs>
                {/* id DIVERSO da quello del logo in sidebar: due gradienti con lo
                    stesso id nella stessa pagina si calpestano a vicenda. */}
                <linearGradient id="lfTypingGrad" x1="2" y1="3" x2="22" y2="21" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#2E6DB1" />
                    <stop offset="0.55" stopColor="#9333EA" />
                    <stop offset="1" stopColor="#C5D042" />
                </linearGradient>
            </defs>
            <path
                d="M5 3.5h14A2.5 2.5 0 0 1 21.5 6v8A2.5 2.5 0 0 1 19 16.5h-8.5L6 20.5v-4H5A2.5 2.5 0 0 1 2.5 14V6A2.5 2.5 0 0 1 5 3.5z"
                fill="url(#lfTypingGrad)"
            />
            {/* I tre puntini del marchio: il CSS li accende a turno (nth-of-type). */}
            <circle className="chatbot-typing-dot" cx="8.5" cy="10" r="1.25" fill="#fff" />
            <circle className="chatbot-typing-dot" cx="12" cy="10" r="1.25" fill="#fff" />
            <circle className="chatbot-typing-dot" cx="15.5" cy="10" r="1.25" fill="#fff" />
        </svg>
    );
}

/** Etichetta di stato dentro la bolla. `label` = toolStatus, se c'è. */
// Le frasi d'attesa cambiano col tempo (task 504). GPT-5 ragiona in silenzio anche per
// minuti prima di mandare la prima parola o i file da generare: senza un segno di vita
// la chat sembra bloccata e l'utente ricarica la pagina. Niente contatore dei secondi:
// un numero che sale sottolinea la lentezza invece di rassicurare.
const ELABORA_DOPO_S = 10;
const SPIEGA_ATTESA_DOPO_S = 30;

// Secondi da quando il componente è comparso (aggiornati ogni secondo).
function useSecondiDaMontaggio() {
    const [secondi, setSecondi] = useState(0);
    useEffect(() => {
        const inizio = Date.now();
        const t = setInterval(() => setSecondi(Math.floor((Date.now() - inizio) / 1000)), 1000);
        return () => clearInterval(t);
    }, []);
    return secondi;
}

export function TypingLabel({ label }) {
    // Resta montato mentre cambia l'etichetta ("Sto generando il file…"): le fasi
    // contano da quando è iniziata l'attesa, non dall'ultimo cambio.
    const secondi = useSecondiDaMontaggio();
    const testo =
        label ||
        (secondi >= SPIEGA_ATTESA_DOPO_S
            ? "Sto ancora lavorando: con molti allegati o file da generare può volerci qualche minuto…"
            : secondi >= ELABORA_DOPO_S
              ? "Sto elaborando la richiesta…"
              : "sta scrivendo…");
    return (
        // role="status" + aria-live: uno screen reader annuncia il cambio di stato
        // ("Sto leggendo la pagina…") senza rubare il focus all'utente.
        <span className="chatbot-typing" role="status" aria-live="polite">
            {/* La key è voluta: quando l'etichetta cambia React rimonta lo span e
                l'animazione di comparsa riparte, così il passaggio da "sta scrivendo…"
                a "Sto leggendo la pagina…" si nota. */}
            <span key={testo} className="chatbot-typing-label">
                {testo}
            </span>
        </span>
    );
}

// Dopo quanti ms senza nuove parole il testo già scritto si considera "in pausa".
const PAUSA_MS = 3000;

/**
 * Indicatore SOTTO il testo già arrivato, quando la risposta si ferma a metà (task 504).
 * OpenAI trattiene le ultime parole di una frase mentre il modello prepara una chiamata
 * a uno strumento (verificato: 11 s per 2 file, minuti per 5 capitoli) e le rilascia
 * insieme alla chiamata. Senza questo la bolla resta su "Al termine ti confermo i" e
 * sembra congelata. Compare dopo PAUSA_MS senza parole nuove, o subito se c'è uno stato
 * ("Sto generando il file…"); sparisce appena riprende il testo.
 */
export function PausaScrittura({ testo, label }) {
    const [ultimoCambio, setUltimoCambio] = useState(() => Date.now());
    const [, setTick] = useState(0);
    useEffect(() => {
        setUltimoCambio(Date.now());
    }, [testo]);
    useEffect(() => {
        const t = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(t);
    }, []);
    if (!label && Date.now() - ultimoCambio < PAUSA_MS) return null;
    return (
        <div className="chatbot-typing-pausa">
            <TypingLabel label={label} />
        </div>
    );
}

export default TypingLabel;
